import { mapState, mapGetters } from 'vuex';
import ApiService from '@/services/apiService';
import { randomPassword, isNullOrEmpty } from '@/utils/vuex';
import scopeBreadcrumbMixin from '@/components/core/tenant/scopeBreadCrumbMixin';
import userAccessPermissionMixin from '@/components/core/tenant/userAccessPermissionMixin';
import pullAt from 'lodash/pullAt';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import uniq from 'lodash/uniq';
export default {
  name: 'ViewAccountMgmtAccountMgmtUserDetails',
  mixins: [scopeBreadcrumbMixin, userAccessPermissionMixin],
  data: function () {
    return {
      tenantDetails: null,
      userKeyValue: [],
      showAddressData: false,
      addressPanel: null,
      loading: false,
      idPanel: null,
      contactPanel: null,
      valid: false,
      isLoading: false,
      originalUser: null,
      editedUser: {
        firstName: null,
        lastName: null,
        email: null,
        password: '',
        active: true,
        properties: {
          address: {
            street: null,
            houseNumber: null,
            postalCode: null,
            city: null,
            region: null,
            country: null
          },
          contactInfo: {
            phone: null,
            mobile: null
          },
          values: [{
            key: '',
            value: ''
          }]
        },
        scope: null,
        access: [
          // {
          //   tenantId: null,
          //   scopeId: null,
          //   Scope: {
          //       id: null,
          //   },
          //   roles: [{
          //           id: null,
          //           name: null
          //       }
          //   ]
          // }
        ]
      },
      uiThemes: [],
      defaultPasswordPolicy: {}
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'inpOutlined', 'inpFilled', 'color', 'UiTheme', 'permissions']),
    ...mapState('authentication', ['user', 'isImpersonating']),
    ...mapGetters({
      sessionUserId: 'authentication/userId'
    }),
    tenantId() {
      return this.$route.params.tenantId;
    },
    requiredRule() {
      return [v => !!v || this.$i18n_t('Common.fieldRequired')];
    },
    passwordRules() {
      return [v => !!v || this.$i18n_t('accMgmt.user.passwordErrors.USER_PASSWORD_REQUIRED'), v => v.length >= this.defaultPasswordPolicy.length || this.$i18n_tc('accMgmt.user.passwordErrors.USER_PASSWORD_MINLENGTH', this.defaultPasswordPolicy.length, {
        n: this.defaultPasswordPolicy.length
      })];
    },
    emailRules() {
      return [v => !!v || this.$i18n_t('Common.fieldRequired'), v => /.+@.+\..+/.test(v) || this.$i18n_t('Common.emailValid')];
    },
    headerButtons() {
      let buttons = [];
      if (this.editedUser.active && this.canImpersonateUser(this.editedUser.access)) {
        buttons.push({
          icon: 'fal fa-user-ninja',
          dataCy: 'btn_imporsonate_user',
          title: this.$i18n_t('accMgmt.tenant.impersonateUser'),
          valid: !this.isImpersonating,
          action: 'impersonate'
        });
      }
      buttons.push({
        icon: 'fas fa-save',
        dataCy: 'btn_save_user',
        title: this.$i18n_t('Common.save'),
        type: 'submit',
        action: 'saveData'
      });
      buttons.push({
        icon: 'fal fa-times',
        dataCy: 'btn_cancel_edit_user',
        color: 'danger',
        action: 'cancel',
        tooltip: this.$i18n_t('Common.cancel')
      });
      return buttons;
    }
  },
  watch: {},
  mounted() {
    this.$store.state.app.loading = false;
    // first wait until all session-users access-scopes and hierarchies have been loaded/cached
    this.doFetchSessionAccessDetails().then(() => {
      return this.fetchTenantData();
    }).then(() => {
      // then fetch details about the to-be-edited user
      if (this.$route.params.id) {
        return ApiService.getUser(this, this.$route.params.id).then(async data => {
          const allUsersScopes = uniq(data.access.map(access => access.scopeId));
          this.uiThemes = data.access.map(a => a.Tenant);
          // fetch all scope-details for all accesses of the edited user
          // this includes all ancestor-info for all scopes as well
          await Promise.all(allUsersScopes.map(scopeId => {
            return this.fetchScopeDetails({
              id: scopeId
            });
          })).catch(() => {
            /* */
          });
          this.setUserDetails(data);
        }).finally(() => {
          this.$store.state.app.loading = false;
        });
      } else {
        this.setUserDetails({
          active: true
        });
      }
    }).catch(e => {
      console.error('Error during details-fetch', e);
    });
  },
  beforeDestroy() {
    this.$router.push({
      path: `/account-mgmt/${this.$route.query.active || this.$route.params.tenantId}?userlist`
    });
  },
  methods: {
    dataCyFromPropertyKey(key, name) {
      const propName = key.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return propName ? `${name}_${propName}` : undefined;
    },
    cancelUserEdit() {
      this.$router.push({
        path: `/account-mgmt/${this.$route.params.tenantId}?userlist`
      });
    },
    requiredRuleKeyValue(kv) {
      return [v => isNullOrEmpty(kv.key) && isNullOrEmpty(kv.value) || !!v || this.$i18n_t('Common.fieldRequired')];
    },
    fetchDefaultRoles(scopeId) {
      ApiService.fetchDefaultRoles(scopeId).then(roles => {
        this.editedUser.access = [{
          tenantId: get(roles, '[0].tenantId') || this.$route.params.tenantId,
          scopeId: scopeId,
          Scope: {
            id: scopeId
          },
          roles,
          primary: true
        }];
        this.editedUser = cloneDeep(this.editedUser);
      }).catch(() => {
        this.$snack(this.$i18n_t('accMgmt.user.createFailedSnack'), 'error');
      });
    },
    fetchTenantData() {
      return Promise.all([ApiService.tenantGetPasswordPolicy(),
      // NOTE: using `no-descendents` to not get any child-data; this will ALSO _NOT_ load any scope-descendents
      ApiService.tenantGetById(this.$route.params.tenantId, ['no-descendents'])]).then(data => {
        this.defaultPasswordPolicy = data[0];
        const tenant = data[1];
        this.tenantDetails = tenant;
        return tenant;
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack('Failed to fetch tenant', 'error');
        }
        this.$router.push({
          path: `/account-mgmt/${this.$route.params.tenantId}?userlist`
        });
      });
    },
    validate() {
      this.valid = this.$refs && this.$refs.form ? this.$refs.form.validate() : false;
      if (this.valid) {
        this.snackbar = true;
      }
    },
    handleSubmit() {
      this.validate();
      if (this.valid) {
        this.saveUser(this.editedUser);
      }
    },
    saveUser(userInfo) {
      this.loading = true;
      userInfo.properties.values = get(this, 'userKeyValue', []).filter(kv => {
        return kv.key !== '' && kv.value !== '';
      });
      if (userInfo.id) {
        this.updateUser(userInfo);
      } else {
        this.createUser(userInfo);
      }
    },
    createUser(user) {
      // as username, use the email. It is the same anyway
      user.username = user.email;
      ApiService.createUser(this.$route.params.tenantId, user).then(() => {
        this.$snack(this.$i18n_t('accMgmt.user.createSuccessSnack'), 'success');
        this.$router.push({
          path: `/account-mgmt/${this.$route.params.tenantId}?userlist`
        });
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('accMgmt.user.createFailedSnack'), 'error');
        }
      }).finally(() => {
        this.loading = false;
      });
    },
    updateUser(user) {
      ApiService.updateUser(user).then(() => {
        return ApiService.updateUserAccess(user, this.originalUser);
      }).then(() => {
        this.$snack(this.$i18n_t('accMgmt.user.editSuccessSnack'), 'success');
        this.$router.push({
          path: `/account-mgmt/${this.$route.params.tenantId}?userlist`
        });
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('accMgmt.user.editFailedSnack'), 'error');
        }
        console.error('Error updating user access', error);
      }).finally(() => {
        this.loading = false;
      });
    },
    addKeyValuePair() {
      this.userKeyValue = cloneDeep(this.userKeyValue.concat([{
        key: '',
        value: ''
      }]));
    },
    removeKeyValuePair(index) {
      pullAt(this.userKeyValue, [index]);
      if (this.userKeyValue.length === 0) {
        this.addKeyValuePair();
      }
      // trigger a UI refresh
      this.userKeyValue = cloneDeep(this.userKeyValue);
    },
    setUserDetails(details) {
      this.originalUser = cloneDeep(details);
      this.editedUser = cloneDeep(details);
      this.editedUser.password = randomPassword();
      if (!get(this.editedUser, 'properties.address')) {
        set(this.editedUser, 'properties.address', {});
      }
      if (!get(this.editedUser, 'properties.contactInfo')) {
        set(this.editedUser, 'properties.contactInfo', {});
      }
      if (!get(this.editedUser, 'properties.tenantIdThemeSetting')) {
        set(this.editedUser, 'properties.tenantIdThemeSetting', get(details, 'access[0].Tenant'));
      }
      if (!get(this.editedUser, 'access')) {
        set(this.editedUser, 'access', []);
      }
      if (!isArray(get(this.editedUser, 'properties.values')) || get(this.editedUser, 'properties.values', []).length === 0) {
        if (get(this.tenantDetails, 'data.userDefaultProperties') && get(this.tenantDetails, 'data.userDefaultProperties').length) {
          this.editedUser.properties.values = get(this.tenantDetails, 'data.userDefaultProperties', []).map(key => {
            return {
              key,
              value: ''
            };
          });
        } else {
          this.editedUser.properties.values = [{
            key: '',
            value: ''
          }];
        }
      }
      this.userKeyValue = this.editedUser.properties.values;
      this.validate();
    },
    updatedUserAccess(access) {
      this.editedUser.access = access;
      if ((access || []).length === 0) {
        delete this.editedUser.scope;
      }
    }
  }
};